/**
 * StudyRooms — /study hub. Lists all peer-created study rooms across the
 * platform. Any authenticated learner can spin up a new room from here or
 * join an active one. Rooms scoped to a specific lesson/course surface
 * that context so you can pick a room that matches your interest.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import ToolSelector from '../components/ToolSelector';
import AdSlot from '../components/AdSlot';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Video, Users, Plus, Sparkles, ArrowRight, CircleDot, Loader2, Search,
  Info,
} from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

const RoomCard = ({ room, onOpen }) => {
  const isLive = room.status === 'live';
  return (
    <article
      className="pro-card p-4 hover:shadow-md transition-shadow cursor-pointer group"
      onClick={onOpen}
      data-testid={`study-room-card-${room.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isLive ? 'bg-rose-500 text-white' : 'bg-primary/10 text-primary'}`}>
          {isLive ? <CircleDot className="w-5 h-5 animate-pulse" /> : <Video className="w-5 h-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {isLive
              ? <span className="inline-flex items-center gap-1 rounded-full text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 bg-rose-500 text-white">Live now</span>
              : <span className="inline-flex items-center gap-1 rounded-full text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 bg-primary/15 text-primary">Open</span>}
            <span className="text-[11px] text-muted-foreground">
              Started {(() => {
                try { return formatDistanceToNow(parseISO(room.created_at || room.scheduled_start), { addSuffix: true }); }
                catch (_) { return 'recently'; }
              })()}
            </span>
          </div>
          <div className="font-heading font-extrabold text-base truncate group-hover:text-primary transition-colors">
            {room.title}
          </div>
          {room.description && (
            <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{room.description}</div>
          )}
          <div className="mt-2 flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Users className="w-3 h-3" /> {room.live_attendee_count || 0} in room
            </span>
            <span>·</span>
            <span className="truncate">Host {room.host_name?.split('@')[0] || 'anonymous'}</span>
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary flex-shrink-0 self-center" />
      </div>
    </article>
  );
};

const StudyRooms = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', duration_minutes: 60 });

  const fetchRooms = async () => {
    try {
      const { data } = await axios.get(`${API}/live-sessions?scope=upcoming&session_type=study_room`);
      setRooms(data?.sessions || []);
    } catch (_) { toast.error('Could not load study rooms'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchRooms();
    const t = setInterval(fetchRooms, 15000);
    return () => clearInterval(t);
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rooms;
    return rooms.filter((r) =>
      (r.title || '').toLowerCase().includes(needle)
      || (r.description || '').toLowerCase().includes(needle)
      || (r.host_name || '').toLowerCase().includes(needle));
  }, [rooms, q]);

  const create = async () => {
    if (!form.title.trim()) { toast.error('Give the room a title'); return; }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/live-sessions`, {
        title:            form.title.trim(),
        description:      form.description.trim(),
        scheduled_start:  new Date().toISOString(),
        duration_minutes: Math.max(15, Math.min(240, Number(form.duration_minutes) || 60)),
        session_type:     'study_room',
      });
      toast.success('Study room created');
      setShowCreate(false);
      navigate(`/live/${data.id}/room`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create room');
      setCreating(false);
    }
  };

  const live = filtered.filter((r) => r.status === 'live');
  const scheduled = filtered.filter((r) => r.status !== 'live');

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="study-rooms-page">
      <ToolSelector />
      <div className="max-w-[1200px] mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div>
            <span className="eyebrow">Community · Study rooms</span>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">
              Study together
            </h1>
            <p className="text-muted-foreground text-sm max-w-2xl mt-1">
              Learner-hosted rooms with whiteboard, breakout rooms and chat — powered by Jitsi. Join an
              open room or start your own for group revision, quiz prep, or troubleshooting a batch.
            </p>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="rounded-full font-bold" data-testid="create-study-room-btn">
                <Plus className="w-4 h-4 mr-1.5" /> New study room
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Start a study room</DialogTitle>
                <DialogDescription>
                  Everyone with the link can join. The room lasts as long as you want — leave anytime.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <Input
                  placeholder="Room title (e.g. Freeze-dry math practice)"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="study-room-title-input"
                  maxLength={80}
                />
                <Textarea
                  placeholder="Optional — what will you review together?"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  maxLength={500}
                />
                <div className="flex items-center gap-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Duration (min)</label>
                  <Input
                    type="number"
                    className="w-28"
                    min={15}
                    max={240}
                    value={form.duration_minutes}
                    onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-full">Cancel</Button>
                <Button onClick={create} disabled={creating} className="rounded-full font-bold" data-testid="submit-study-room-btn">
                  {creating ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> creating…</> : <>Start room</>}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div>
            {/* Search */}
            <div className="relative mb-4">
              <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Search rooms by title, host or topic…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9"
                data-testid="study-rooms-search"
              />
            </div>

            {loading ? (
              <div className="text-muted-foreground text-sm inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> loading rooms…
              </div>
            ) : filtered.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center">
                <Sparkles className="w-8 h-8 mx-auto text-muted-foreground/60 mb-2" />
                <div className="font-heading font-extrabold">Nobody's studying right now</div>
                <p className="text-sm text-muted-foreground mt-1">Be the first — click "New study room" and share the link with your classmates.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {live.length > 0 && (
                  <section>
                    <h2 className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
                      Live now ({live.length})
                    </h2>
                    <div className="grid gap-3">
                      {live.map((r) => <RoomCard key={r.id} room={r} onOpen={() => navigate(`/live/${r.id}/room`)} />)}
                    </div>
                  </section>
                )}
                {scheduled.length > 0 && (
                  <section>
                    <h2 className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
                      Open ({scheduled.length})
                    </h2>
                    <div className="grid gap-3">
                      {scheduled.map((r) => <RoomCard key={r.id} room={r} onOpen={() => navigate(`/live/${r.id}/room`)} />)}
                    </div>
                  </section>
                )}
              </div>
            )}
          </div>

          {/* Right rail */}
          <aside className="space-y-4">
            <div className="rounded-2xl border border-border bg-card p-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-2 mb-2 text-foreground">
                <Info className="w-3.5 h-3.5 text-primary" />
                <span className="font-heading font-extrabold text-sm">How study rooms work</span>
              </div>
              <ul className="space-y-1.5 list-disc pl-4">
                <li>Rooms are peer-hosted — any learner can create one.</li>
                <li>They include whiteboard, breakout rooms and chat.</li>
                <li>Community guidelines apply — be kind, be helpful.</li>
              </ul>
            </div>
            <AdSlot location="live_hub_side" variant="sidebar" />
          </aside>
        </div>
      </div>
    </div>
  );
};

export default StudyRooms;
